// The manifest's schemas, narrowed for reading.
//
// **Hand-written, and it has to be.** Every other authoring type in this
// client is generated from `authoring.yaml`, and nothing outside
// `src/api/` may declare one. A schema is the exception the contract
// itself names: it is declared there as a free-form object, because
// OpenAPI 3.0 cannot describe a recursive JSON Schema subset without
// either flattening it to `any` or pinning a shape that the Go reflector
// would then have to be kept in step with by hand. So the generated type
// is `{ [key: string]: any }` and this file is where it gets a shape.
//
// That makes this the one place a wire-format change here would be
// noticed by a type error rather than by an empty form, which is why the
// reading helpers live here too and no component parses a schema itself.

/**
 * One shape, as the manifest publishes it — a subset of JSON Schema plus
 * its extensions.
 *
 * The extensions are not decoration. `x-order` restores the field order
 * the Go struct already declared, which JSON Schema objects cannot carry;
 * `x-link` and `x-child` are what let this client tell a *field* intent
 * from a *structural* one without holding a list of which is which; and
 * `x-slot` and `x-position` are that same split one level finer, naming
 * the members a placement control fills on the author's behalf.
 */
export interface Schema {
  type?: 'object' | 'array' | 'string' | 'integer' | 'number' | 'boolean'
  properties?: Record<string, Schema>
  required?: string[]
  items?: Schema
  additionalProperties?: Schema | boolean
  enum?: string[]
  format?: string
  /** The shortest string the platform accepts. Absent, an empty one is a value. */
  minLength?: number
  /**
   * What the document holds for this field before it is first written.
   *
   * Only a *declaration* has one — a schema reflected off a Go struct has
   * a zero value, which is not the same claim — so a control shows it as
   * what would apply rather than filling it in. Sending it back would
   * turn "the author left this alone" into "the author chose this", and
   * the two differ the day the default moves.
   */
  default?: unknown
  $ref?: string
  /** Only ever on a root schema, and only when some shape contains itself. */
  $defs?: Record<string, Schema>
  'x-order'?: number
  'x-link'?: boolean
  'x-child'?: boolean
  'x-slot'?: boolean
  'x-from-slot'?: boolean
  'x-position'?: boolean
  /**
   * The string is a component's id, and the value is the role a
   * candidate must declare. A control offers the tenant's components of
   * that role; a text box would ask an author for a UUID.
   */
  'x-component-role'?: string
  /**
   * The string is a type's *name*, and the value is the role that type
   * must declare. A control offers the manifest's types of that role; any
   * other name is refused.
   */
  'x-type-role'?: string
  /**
   * The object is a reference the payload writes, and the value is the
   * name of the holder type's edge it writes under. A control offers what
   * that edge accepts, from the holder's `references` in the manifest; an
   * edge id, a name and a UUID are not things an author types.
   */
  'x-edge'?: string
  /**
   * When this property applies at all: the sibling named by `field`
   * holds one of `is`. Absent means it always applies, which is every
   * property of every shape that declares no discriminator.
   *
   * It is what closes the gap a create dialog used to leave open. A Link
   * is one of four kinds and each kind has its own address field; a
   * Media is internal or external and only one of them has a url. The
   * fold refused every other combination with an accurate sentence, and
   * this client — which derives its forms from the schema and holds no
   * list of which field belongs to which kind — drew all of them at
   * once. So an author picked the wrong box and was told so afterwards,
   * by a message about a field the form had offered them.
   */
  'x-when'?: Condition
  /**
   * Delivery never serves this property: it is stored and authored, and
   * absent from every page a visitor is sent. Anything reading a document
   * as its visitors would leaves it out.
   */
  'x-concealed'?: boolean
}

/**
 * The extensions that mark a value as machinery rather than words.
 *
 * Each one is the platform saying "an author never types this": a link
 * id, a region, a position, a type's name, a reference. A grid's cells
 * are the case that found it — each is a link id and a span, and read as
 * words they printed the grid's own placement names as a list.
 */
const MACHINERY = ['x-link', 'x-child', 'x-slot', 'x-from-slot', 'x-position', 'x-type-role', 'x-edge'] as const

/** Whether a schema describes machinery a reader is never shown. See `MACHINERY`. */
export function isMachinery(schema: Schema): boolean {
  return MACHINERY.some((marker) => Boolean(schema[marker]))
}

/**
 * One property's condition: a sibling's name, and the values it applies
 * for — or, where `in` is set, a member of the shape `in` names.
 */
export interface Condition {
  in?: string
  field: string
  is: string[]
}

/** A property of an object schema, with everything a control needs. */
export interface Property {
  name: string
  schema: Schema
  required: boolean
}

/**
 * Narrow the manifest's free-form schema value.
 *
 * The generated type is `any`-shaped, so this is the boundary: past it,
 * a schema is a `Schema` and nothing re-checks.
 */
export function asSchema(value: unknown): Schema | undefined {
  return value && typeof value === 'object' ? (value as Schema) : undefined
}

/**
 * Follow a `$ref` to the definition it names.
 *
 * `$defs` lives on the **root** schema only — the one the manifest
 * published — so resolution always needs both, and a component holding a
 * nested schema without its root cannot resolve one. That is why every
 * helper here takes the root as well.
 *
 * A ref appears only where a shape contains itself, which today is a
 * website's routing tree and nothing else. An unresolvable one returns
 * undefined rather than throwing: a client meeting a schema from a newer
 * build should render what it understands and omit what it does not.
 */
export function resolve(schema: Schema | undefined, root: Schema): Schema | undefined {
  if (!schema) return undefined
  if (!schema.$ref) return schema

  const prefix = '#/$defs/'
  if (!schema.$ref.startsWith(prefix)) return undefined
  return root.$defs?.[schema.$ref.slice(prefix.length)]
}

/**
 * An object's properties in the order its Go struct declared them.
 *
 * Sorted by `x-order` rather than by key, because the declaration order
 * is a decision somebody made — alt before title before caption — and
 * alphabetising it would replace that decision with an accident. A
 * property missing `x-order` sorts last rather than first, so a schema
 * from a build that did not emit it degrades to "the ordered ones, then
 * the rest" instead of scrambling everything.
 */
export function orderedProperties(schema: Schema | undefined, root: Schema): Property[] {
  const resolved = resolve(schema, root)
  if (!resolved?.properties) return []

  const required = new Set(resolved.required ?? [])
  return Object.entries(resolved.properties)
    .map(([name, property]) => ({ name, schema: property, required: required.has(name) }))
    .sort((left, right) => order(left.schema) - order(right.schema))
}

function order(schema: Schema): number {
  return schema['x-order'] ?? Number.MAX_SAFE_INTEGER
}

/**
 * Whether one property applies to the object as it currently stands.
 *
 * **The question a generated form could not ask before.** A property
 * with no condition always applies, which is nearly all of them; a
 * conditioned one applies while its named sibling holds one of the
 * values it names.
 *
 * A discriminator that has not been answered yet — a new Link before a
 * kind is chosen — makes every conditioned property inapplicable, and
 * that is the wanted answer rather than a degenerate one: the form asks
 * the deciding question first and grows the box that goes with it. It is
 * the same shape the pattern setup already has, where choosing a pattern
 * is what makes the second control meaningful.
 *
 * The sibling is read from the *object*, never from the schema, because
 * the condition is about what the author is writing rather than about
 * what the type can hold.
 */
export function applies(property: Schema, value: Record<string, unknown>): boolean {
  return holds(property['x-when'], value)
}

/**
 * Whether a condition is satisfied by the object it is read against.
 *
 * Shared with the manifest's `uploadWhen`, which is the same claim about
 * the same document in a different place: a Media's type holds a file
 * and an external Media does not, and a client that drew an uploader
 * from the type alone offered a control the surface refuses a grant for.
 * One reading, so the form and the uploader cannot disagree.
 *
 * No condition is satisfied by everything, which is what "every
 * component of this type" and "this property always applies" both mean.
 */
export function holds(condition: Condition | undefined, value: Record<string, unknown>): boolean {
  if (!condition) return true
  // One member down where the condition says so: a Container's background
  // picture is the Container's edge, decided by `background.kind`. An
  // absent or non-object member answers nothing, which refuses.
  let shape: unknown = value
  if (condition.in) {
    shape = value[condition.in]
    if (typeof shape !== 'object' || shape === null || Array.isArray(shape)) return false
  }
  const held = (shape as Record<string, unknown>)[condition.field]
  return typeof held === 'string' && condition.is.includes(held)
}

/**
 * An object's properties in declaration order, with the ones that do not
 * apply to this value left out.
 *
 * Every form goes through this rather than through `orderedProperties`,
 * so "which controls are drawn" and "which members survive a change" are
 * one answer. Two would put back the failure this exists to prevent, in
 * its worse direction: a control drawn for a member the form then strips
 * on submit.
 */
export function applicableProperties(
  schema: Schema | undefined,
  root: Schema,
  value: Record<string, unknown>,
): Property[] {
  return orderedProperties(schema, root).filter((property) => applies(property.schema, value))
}

/**
 * The object with every member that no longer applies removed.
 *
 * **Hiding a control is not enough.** An author who types an address,
 * then changes the kind, leaves the address behind in the form's state,
 * and sending it is exactly the contradiction the fold refuses — "an
 * internal link states an address" over a box that is no longer on the
 * screen. So the value is pruned wherever it changes, and what is sent
 * is what the form was showing.
 *
 * Nested objects are pruned with their own discriminator, which is what
 * a region's background needs: its kind is a member of the background,
 * not of the object holding it.
 */
export function pruneInapplicable(
  schema: Schema | undefined,
  root: Schema,
  value: Record<string, unknown>,
): Record<string, unknown> {
  const resolved = resolve(schema, root)
  if (!resolved?.properties) return value

  const pruned: Record<string, unknown> = {}
  for (const [name, member] of Object.entries(value)) {
    const property = resolved.properties[name]
    // A member the schema does not describe rides through untouched, on
    // the same rule every control here follows: this form reports the
    // whole object, and dropping what it does not understand would clear
    // it on the wire.
    if (!property) {
      pruned[name] = member
      continue
    }
    if (!applies(property, value)) continue
    pruned[name] = isObject(member) ? pruneInapplicable(property, root, member) : member
  }
  return pruned
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Whether a shape names part of the component tree — a placement being
 * made, or a link id addressing one.
 *
 * **This is the split the whole generated-editor mechanism rests on.** A
 * payload of scalars and vocabularies is a form: the client can draw it
 * for a type it has never seen, because there is nothing to know beyond
 * what the schema says. A payload that carries a child or a link id is
 * not, and drawing one would hand an author a text box for a link id —
 * a control that can only produce a command the fold refuses, or worse,
 * one it accepts against the wrong placement.
 *
 * So it is computed here, from markers on the wire, rather than held as a
 * list of structural intent names. A list would be a second declaration
 * of something the payload already says, and a tenth component type's
 * structural intents would be missing from it.
 */
export function namesTheTree(schema: Schema | undefined, root: Schema): boolean {
  return walk(schema, root, new Set())

  function walk(current: Schema | undefined, rootSchema: Schema, seen: Set<string>): boolean {
    if (!current) return false

    // Guard the cycle before resolving it. A recursive shape reaches
    // itself, and this walk must terminate on a schema that legitimately
    // does — a website's routing tree is not structural, it is deep.
    if (current.$ref) {
      if (seen.has(current.$ref)) return false
      seen.add(current.$ref)
    }

    const resolved = resolve(current, rootSchema)
    if (!resolved) return false
    if (resolved['x-child'] || resolved['x-link']) return true

    if (walk(resolved.items, rootSchema, seen)) return true
    if (typeof resolved.additionalProperties === 'object' && walk(resolved.additionalProperties, rootSchema, seen)) {
      return true
    }
    return Object.values(resolved.properties ?? {}).some((property) => walk(property, rootSchema, seen))
  }
}

/**
 * Whether this client has a control for one property.
 *
 * **The single source of the control mapping's existence.** `SchemaForm`
 * gates on this before its switch, and `isFormable` counts with it, so
 * the question "will anything be drawn for this?" is answered in one
 * place. Two answers would produce the failure this exists to prevent: a
 * section that offers a form, draws nothing, and leaves a submit button
 * that can never be pressed.
 *
 * **An array is drawable exactly when its elements are plain strings.**
 * That is the list an author writes — a select's options, a form's
 * recipients, a declared type's slots — and `OptionList` adds to it and
 * removes from it without claiming to know what its order means. An array
 * of anything else is not: a list of records needs a control that knows
 * which member identifies a row, and a list carrying an enum is a
 * multiple choice rather than a list of values, which is a different
 * control this client does not have.
 *
 * The exclusion this leaves in place is worth naming, because it was the
 * reason the whole case used to be refused: `form.fields_reordered`
 * carries a bare `[]string` that *names existing fields* rather than
 * holding values, and drawing it would offer an author a box to type a
 * field name into. It is not reachable — `FormEditor` sends that intent
 * from its own drag handles — but nothing in the schema says so, which is
 * the limit of what this function can see.
 */
export function isDrawable(schema: Schema | undefined, root: Schema): boolean {
  const resolved = resolve(schema, root)
  if (!resolved) return false
  if (resolved.enum) return true
  // A reference is drawn as a picker, whatever its members are.
  if (resolved['x-edge']) return true

  switch (resolved.type) {
    case 'string':
    case 'integer':
    case 'number':
    case 'boolean':
      // Every string is drawable, including the ones carrying a
      // `format`: rich text gets the editor a Content body gets, and a
      // date gets a date input. A format this client does not recognise
      // still draws a text box, which is the honest degradation — the
      // value is a string and the author can write one.
      return true
    case 'object':
      return Object.keys(resolved.properties ?? {}).length > 0
    case 'array': {
      // The items schema rather than the array's own, and resolved
      // through the root, because a `$ref` is how a schema names the
      // shape it holds. A plain string, and nothing more decorated than
      // one: an enum here would be a choice among declared values and a
      // format would be a control this list does not draw.
      const items = resolve(resolved.items, root)
      return items?.type === 'string' && !items.enum && items.format === undefined
    }
    default:
      return false
  }
}

/**
 * Whether this client can draw a usable form for a shape.
 *
 * Three conditions, and the third was found by building it. An object;
 * naming no part of the tree; and carrying **at least one property this
 * client can actually draw**.
 *
 * That third one is not hypothetical. `form.fields_reordered` carries a
 * bare `[]string` — the order of a Form's own inputs, which are named
 * rather than linked — so it trips neither marker and is still not a
 * form: every member is an array, so the section would render a heading,
 * no controls, and a submit button permanently disabled because nothing
 * could ever make it dirty. It belongs with the intents this client has
 * no control for, which is a different statement from "it names a
 * placement" and is reported as one.
 *
 * **Some** rather than every, deliberately: an intent carrying a title
 * and a list is worth drawing, because the form edits the title and
 * restates the list exactly as it found it.
 *
 * An object with no properties at all is formable and is a *button* — a
 * command with nothing to state, like `website.not_found_cleared`. It has
 * nothing undrawable in it, so nothing is being hidden from an author.
 */
export function isFormable(schema: Schema | undefined, root: Schema): boolean {
  const resolved = resolve(schema, root)
  if (!resolved) return false
  if (resolved.type !== 'object') return false
  if (namesTheTree(resolved, root)) return false

  const properties = orderedProperties(resolved, root)
  return properties.length === 0 || properties.some((property) => isDrawable(property.schema, root))
}

/**
 * What a structural intent *does* to the tree, as far as its payload can
 * say.
 *
 * Three roles, and each one is a different control: `place` makes a
 * placement and needs the picker; `act` names a placement that already
 * exists and belongs on that placement's row; `arrange` names an order of
 * them and belongs to the buttons that move one.
 */
export type StructuralRole = 'place' | 'act' | 'arrange' | 'move'

/**
 * One structural intent, taken apart into the members a control supplies
 * and the members an author still fills in.
 *
 * The member *names* are read off the markers rather than assumed — this
 * client never looks for a property called `slot`, it looks for the one
 * marked `x-slot` — which is what lets a type spell its payload however
 * its Go struct reads and still get a control.
 */
export interface Structural {
  role: StructuralRole
  /** The placement being made. Present exactly when the role is `place`. */
  child?: string
  /**
   * A link id the control supplies. On an `act` it is the placement being
   * acted on; on a `place` or an `arrange` it names the *parent* level —
   * `website.page_added` adds a route under one, and
   * `website.routes_reordered` arranges one level of the same tree.
   */
  link?: string
  /** The order of placements. Present exactly when the role is `arrange`. */
  order?: string
  /**
   * Which of the holder's regions, where the holder has more than one.
   * On a `move` it is the region being joined.
   */
  slot?: string
  /**
   * The region a placement is leaving. Present exactly when the role is
   * `move`, and it is what tells a move apart from every other intent
   * that names one placement: two members both name a region, so they
   * cannot share a marker.
   */
  fromSlot?: string
  /** Where in the list. Absent on an intent that replaces rather than inserts. */
  position?: string
  /** What the control does not supply, and an author fills in as a form. */
  rest: Property[]
  /**
   * Members that are neither marked nor drawable, so this client cannot
   * complete the command.
   *
   * Empty for every intent the nine types declare, and reported rather
   * than ignored because the alternative is a control that sends a
   * payload missing a required member and blames the surface for
   * refusing it. It is the same condition `isFormable` grew for the same
   * reason, at the other end of the split.
   */
  unsupported: Property[]
}

/**
 * Take a structural intent's payload apart, or answer undefined for one
 * that is not structural.
 *
 * **The verb is not recoverable and this deliberately does not guess at
 * it.** `website.home_set` and `container.item_removed` both carry one
 * link id and mean opposite things, so `act` is as far as a payload can
 * take a client: what the operation *does* is what its name says, and a
 * control labels the action by humanising that name. Inferring a verb
 * from a shape would be this client deciding that naming one placement
 * means removing it, which is wrong for one of the two the moment a type
 * declares both.
 */
export function structuralOf(schema: Schema | undefined, root: Schema): Structural | undefined {
  const resolved = resolve(schema, root)
  if (!resolved || resolved.type !== 'object') return undefined

  const found: Omit<Structural, 'role'> = { rest: [], unsupported: [] }

  for (const property of orderedProperties(resolved, root)) {
    const member = resolve(property.schema, root)
    if (!member) continue

    if (member['x-child']) found.child ??= property.name
    else if (member['x-link']) found.link ??= property.name
    else if (member['x-from-slot']) found.fromSlot ??= property.name
    else if (member['x-slot']) found.slot ??= property.name
    else if (member['x-position']) found.position ??= property.name
    else if (resolve(member.items, root)?.['x-link']) found.order ??= property.name
    else if (isDrawable(property.schema, root)) found.rest.push(property)
    else found.unsupported.push(property)
  }

  // Ordered, because an intent may carry more than one marker and the
  // stronger fact wins. `website.page_added` carries a child *and* a
  // link, and it is a placement being made — the link names the level it
  // goes under. `website.routes_reordered` carries a link and an order,
  // and it arranges — same reason.
  //
  // A move is told apart before `act`, and only a move can be: it names
  // one placement, exactly as removing one does, and the second region
  // is the whole of the difference. Without that marker `page.block_moved`
  // and `page.block_removed` are the same shape.
  const role: StructuralRole | undefined = found.child
    ? 'place'
    : found.order
      ? 'arrange'
      : found.fromSlot
        ? 'move'
        : found.link
          ? 'act'
          : undefined
  if (!role) return undefined

  return { role, ...found }
}

/**
 * A property's starting value, read out of the document's stored fields.
 *
 * By name, and only by name. An intent's payload members are named after
 * the fields they set — `page.retitled` carries `title`, and the page
 * holds `title` — because both are read off the same Go struct field.
 * Where that does not hold, the control starts empty, which is honest:
 * this client cannot know that a differently-named payload member means
 * the same thing as a stored field, and guessing would prefill a form
 * with a value the intent will not set.
 */
export function storedValue(fields: Record<string, unknown>, property: Property): unknown {
  return fields[property.name]
}
